import type { ReactNode } from "react";

import type {
	FuseInternalPortalUserInfo,
	FuseInternalWebhookGroup,
} from "@shared/fuse-internal-api";

import { createContext, useContext } from "react";

import { setContext } from "@sentry/react";

import { FullScreenLoader } from "../../components/src/full-screen-loader";
import { authQueryClient } from "../../fuse-internal-api/src/client";
import { AuthContext } from "./auth-context";

export type OrgContextType = {
	client_id?: null | string | undefined;
	created_at: string;
	fee_source_account: "primary_account" | "transaction_account" | undefined;
	id: string;
	isMakerCheckerEnabled: boolean;
	name: string;
	onboarding_mode: "direct" | "reliance" | undefined;
	stripe_customer_id?: null | string | undefined;
	user: null | Omit<FuseInternalPortalUserInfo, "permissions">;
	webhook_groups: FuseInternalWebhookGroup[];
};

export const OrgContext = createContext<OrgContextType>({
	client_id: undefined,
	created_at: "",
	fee_source_account: undefined,
	id: "",
	isMakerCheckerEnabled: false,
	name: "",
	onboarding_mode: undefined,
	stripe_customer_id: undefined,
	user: null,
	webhook_groups: [],
});

export const useOrganization = () => {
	return useContext(OrgContext);
};

type OrgProviderProps = {
	children: ReactNode;
	fallback: (err?: string) => ReactNode;
};

export function OrgProvider({ children, fallback }: OrgProviderProps): ReactNode {
	const authContext = useContext(AuthContext);

	const { data, error, isFetched, isLoading } =
		authQueryClient.useInternalGetOrganisationMetadata(
			{},
			{
				refetchOnWindowFocus: false,
				retry: (failureCount, error) => {
					if (error.message.includes("401")) {
						return false;
					}
					return failureCount <= 2;
				},
			},
		);

	if (isLoading || !isFetched) {
		return <FullScreenLoader />;
	}

	if (error) {
		return fallback(error.message.includes("401") ? undefined : error.message);
	}

	if (!data) {
		return fallback("Could not retrieve organisation data. Please try again.");
	}

	// Set the organisation context for Sentry
	setContext("organisation", {
		id: data.id,
		name: data.name,
	});

	const orgData = {
		client_id: data.client_id,
		created_at: data.created_at,
		fee_source_account: data.fee_source_account,
		id: data.id,
		isMakerCheckerEnabled: data.maker_checker_enabled,
		name: data.name,
		onboarding_mode: data.onboarding_mode,
		stripe_customer_id: data.stripe_customer_id ?? "none",
		user: authContext,
		webhook_groups: data.webhook_groups,
	};

	return <OrgContext.Provider value={orgData}>{children}</OrgContext.Provider>;
}
